import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const STATUS_LABELS = {
  approved: { label: "Active", desc: "Your BookFreely account is active and customers can book your published services. Complete payout setup to receive your earnings.", color: "var(--gf-accent)" },
  suspended: { label: "Suspended", desc: "Your account is currently suspended by BookFreely. See the note below or your email for details.", color: "#dc2626" },
  banned: { label: "Removed", desc: "Your account has been removed from BookFreely per our Community Guidelines.", color: "#dc2626" },
  rejected: { label: "Rejected", desc: "Your account cannot accept bookings on BookFreely. See the note below for details.", color: "#dc2626" },
};

/**
 * Stripe Connect payout-setup panel for businesses.
 *
 * Stripe Connect KYC is the sole identity verification and it controls TWO
 * things only: receiving payouts, and the public "Verified" badge. It does NOT
 * gate publishing services, being listed, or accepting bookings — a subscribed
 * business in good standing with a published service is visible and bookable
 * before KYC, and its share of each payment is held as a pending transfer
 * until Stripe reports charges_enabled AND payouts_enabled.
 */
export default function StripeConnectPanel({ user, refreshUser }) {
  const { authHeader } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const refresh = async ({ syncAuth = false } = {}) => {
    try {
      const r = await axios.get(`${API}/business/connect/status`, { headers: authHeader() });
      setStatus(r.data);
      // If we're returning from a Stripe onboarding session (or the caller asks for it),
      // pull the fresh /auth/me so BusinessDashboard's `user.stripe_charges_enabled`,
      // `user.is_verified`, etc. update in the AuthContext IMMEDIATELY — no manual
      // refresh needed. This is the sync the user has been waiting for.
      if (syncAuth && refreshUser) {
        try { await refreshUser(); } catch (_) { /* best-effort */ }
      }
    } catch (e) {
      // Stripe key not configured yet (preview mode). Fall back to local user fields so the
      // page still renders.
      setStatus({
        connected: !!user.stripe_account_id,
        stripe_account_id: user.stripe_account_id,
        charges_enabled: !!user.stripe_charges_enabled,
        payouts_enabled: !!user.stripe_payouts_enabled,
        details_submitted: !!user.stripe_details_submitted,
        admin_status: user.admin_status || "approved",
        admin_notes: user.admin_notes,
        is_verified: !!user.is_verified,
        preview_mode: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Detect Stripe Connect return BEFORE the first status fetch so we can request
    // an auth-context sync on the same round-trip. This is what makes the business
    // "immediately have access" after finishing Stripe onboarding — within ~2s of
    // Stripe redirecting them back, `user.stripe_charges_enabled` flips true and the
    // dashboard drops its "Get verified" gating.
    const params = new URLSearchParams(window.location.search);
    const returning = params.get("stripe") === "return";
    refresh({ syncAuth: returning });
    if (returning) {
      params.delete("stripe");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      // Guard against non-https origins (rare — dev-only). Stripe requires
      // publicly-reachable HTTPS return URLs.
      const origin = window.location.origin;
      if (!/^https:\/\//i.test(origin)) {
        toast.error("Onboarding requires the app to be served over HTTPS. Open BookFreely in your production URL and try again.");
        setStarting(false);
        return;
      }
      const returnUrl = `${origin}/business?stripe=return`;
      const r = await axios.post(
        `${API}/business/connect/onboard`,
        { return_url: returnUrl, refresh_url: returnUrl },
        { headers: authHeader() }
      );
      const url = r?.data?.url;
      if (!url || !/^https:\/\/([a-z0-9-]+\.)?stripe\.com\//i.test(url)) {
        toast.error("Onboarding didn't return a valid URL. Please try again in a moment.");
        setStarting(false);
        return;
      }
      window.location.href = url;
    } catch (e) {
      // Surface Stripe's actual message. Do NOT falsely blame the API key —
      // 99% of the time this is a bad URL or a Stripe account settings issue,
      // not a missing key.
      const detail = e?.response?.data?.detail || "Could not start onboarding";
      toast.error(detail);
      setStarting(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const r = await axios.post(`${API}/business/connect/login`, {}, { headers: authHeader() });
      window.open(r.data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe dashboard");
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border p-5 text-sm" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-muted)" }}>
        Checking verification…
      </div>
    );
  }

  const stripeOk = !!status?.charges_enabled;
  const adminStatusKey = status?.admin_status || "approved";
  const adminMeta = STATUS_LABELS[adminStatusKey] || STATUS_LABELS.approved;
  const accountOk = adminStatusKey === "approved";
  const payoutsOk = !!status?.payouts_enabled;
  const fullyVerified = stripeOk && payoutsOk && accountOk;
  // Plain-English requirement list from the backend — never Stripe's raw
  // dotted codes (e.g. business_profile.mcc, representative.dob.day).
  const friendlyDue = status?.requirements_friendly || [];

  return (
    <div
      data-testid="verification-panel"
      className="mt-6 rounded-2xl border p-5"
      style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Payouts</div>
          <div className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }} data-testid="payout-setup-heading">
            {fullyVerified ? "Payout setup complete" : "Complete payout setup to receive earnings"}
          </div>
          <p className="text-sm mt-1 max-w-xl" style={{ color: "var(--gf-text-muted)" }} data-testid="payout-setup-description">
            Customers can book your published services now. Complete your secure Stripe payout setup before you can receive your earnings. Until then, your earnings will remain held as pending transfers.
          </p>
        </div>
        {fullyVerified && (
          <span
            data-testid="verified-state-badge"
            className="rounded-full px-3 py-1 text-xs font-medium"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            ✓ Verified
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {/* Stripe Connect step */}
        <div
          data-testid="stripe-connect-step"
          className="rounded-xl border p-4"
          style={{ borderColor: stripeOk ? "var(--gf-accent)" : "var(--gf-border-strong)", background: "var(--gf-bg-soft)" }}
        >
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }} data-testid="payout-step-label">STEP 1 · PAYOUT SETUP</div>
            <StepDot ok={stripeOk} />
          </div>
          <div className="font-display text-lg mt-2" style={{ color: "var(--gf-text)" }}>
            {stripeOk ? "Payouts ready" : status?.details_submitted ? "Payout setup in review…" : "Payout setup"}
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
            Your published services stay live and bookable throughout. Payout setup only controls when your held earnings are transferred to you. Securely powered by Stripe.
          </p>

          {!stripeOk ? (
            <button
              data-testid="start-stripe-onboarding-btn"
              onClick={startOnboarding}
              disabled={starting}
              className="mt-4 rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {starting ? "Opening secure setup…" : "Continue payout setup"}
            </button>
          ) : (
            <button
              data-testid="stripe-dashboard-btn"
              onClick={openStripeDashboard}
              className="mt-4 rounded-full px-4 py-2 text-sm border"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              Open Payouts Dashboard
            </button>
          )}

          {friendlyDue.length ? (
            <ul className="text-xs mt-3 list-disc pl-5" data-testid="payout-requirements-friendly" style={{ color: "var(--gf-text-muted)" }}>
              {friendlyDue.slice(0, 4).map((r) => (
                <li key={r}>Still needed: {r}</li>
              ))}
            </ul>
          ) : null}
        </div>

        {/* Account status step — shown only when Stripe is done, and only alarms when there's a problem */}
        <div
          data-testid="admin-review-step"
          className="rounded-xl border p-4"
          style={{ borderColor: accountOk ? "var(--gf-accent)" : "var(--gf-border-strong)", background: "var(--gf-bg-soft)" }}
        >
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Step 2 · Account status</div>
            <StepDot ok={accountOk} bad={!accountOk} />
          </div>
          <div className="font-display text-lg mt-2" style={{ color: adminMeta.color }} data-testid="admin-status-label">
            {adminMeta.label}
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>{adminMeta.desc}</p>

          {status?.admin_notes && (
            <div className="mt-3 rounded-md border p-2 text-xs" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-soft)" }} data-testid="admin-notes">
              <span className="uppercase tracking-[0.18em] text-[10px] block mb-1" style={{ color: "var(--gf-text-muted)" }}>Note from BookFreely</span>
              {status.admin_notes}
            </div>
          )}

          <button
            data-testid="refresh-verification-btn"
            onClick={refresh}
            className="mt-4 text-xs underline"
            style={{ color: "var(--gf-text-muted)" }}
          >
            Refresh status
          </button>
        </div>
      </div>

      {!fullyVerified && accountOk && (
        <div className="mt-5 rounded-md border-l-4 px-4 py-3 text-sm" style={{ borderColor: "var(--gf-accent)", background: "var(--gf-bg-soft)", color: "var(--gf-text-soft)" }} data-testid="account-status-message">
          Your BookFreely account is active and customers can book your published services. Complete payout setup to receive your earnings.
        </div>
      )}
    </div>
  );
}

function StepDot({ ok, pending, warn, bad }) {
  let bg = "var(--gf-border-strong)";
  let ring = "transparent";
  if (ok) { bg = "var(--gf-accent)"; ring = "var(--gf-surface-2)"; }
  else if (warn) { bg = "#d97706"; ring = "rgba(217,119,6,0.25)"; }
  else if (bad) { bg = "#dc2626"; ring = "rgba(220,38,38,0.25)"; }
  else if (pending) { bg = "var(--gf-text-muted)"; }
  return (
    <span
      aria-hidden
      className="inline-block w-3 h-3 rounded-full"
      style={{ background: bg, boxShadow: `0 0 0 4px ${ring}` }}
    />
  );
}
